import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ShareIcon from '@mui/icons-material/Share';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CircleIcon from '@mui/icons-material/Circle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { adminApi } from '../../api/adminApi';
import {
  COMPETITION_STATUS_LABELS,
  type CompetitionStatus,
} from '../../types/domain';
import { CompetitionCategoriesTab } from './CompetitionCategoriesTab';
import { CompetitionTeamsTab } from './CompetitionTeamsTab';
import { CompetitionResultsTab } from './CompetitionResultsTab';
import { CompetitionOverallTab } from './CompetitionOverallTab';
import { CompetitionRecordersTab } from './CompetitionRecordersTab';
import { CompetitionDisplaySettingsTab } from './CompetitionDisplaySettingsTab';
import { CompetitionAuditTab } from './CompetitionAuditTab';
import { CompetitionPenaltyCatalogTab } from './CompetitionPenaltyCatalogTab';
import { CompetitionAthletesTab } from './CompetitionAthletesTab';
import { CompetitionIndividualEntriesTab } from './tabs/CompetitionIndividualEntriesTab';
import { useImpersonation, withUnit } from '../../hooks/useImpersonation';
import { useAuth } from '../../hooks/useAuth';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { formatDateCz } from '../../utils/dateFormat';

const STATUSES: CompetitionStatus[] = ['DRAFT', 'LIVE', 'FINISHED'];
const STATUS_COLOR: Record<CompetitionStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  LIVE: 'error',
  FINISHED: 'success',
};

export const CompetitionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const compId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { effectiveUnitId } = useImpersonation();
  const { user } = useAuth();
  const isRecorder = user?.role === 'RECORDER';
  const backTarget = isRecorder
    ? '/recorder'
    : withUnit('/admin/competitions', effectiveUnitId);
  type MainTab = 'setup' | 'results' | 'overall' | 'sharing' | 'history';
  const [mainTab, setMainTab] = useState<MainTab>('setup');
  // RECORDER vidi jen tab "Druzstva" (value 1) — prvni tab "Kategorie a discipliny" je skryty.
  const [setupTab, setSetupTab] = useState(isRecorder ? 1 : 0);
  const [sharingTab, setSharingTab] = useState(0);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pendingStatus, setPendingStatus] = useState<CompetitionStatus | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    competitionDate: '',
    location: '',
    publicVisible: true,
    publishAt: '',
    landingVisible: true,
    headJudgeName: '',
    eventLeaderName: '',
    commissarName: '',
  });

  const { data: comp, isLoading } = useQuery({
    queryKey: ['admin', 'competition', compId],
    queryFn: () => adminApi.competitionDetail(compId),
    enabled: !!compId,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['admin', 'competition', compId, 'categories'],
    queryFn: () => adminApi.competitionCategories(compId),
    enabled: !!compId,
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: CompetitionStatus) =>
      comp
        ? adminApi.updateCompetition(comp.id, {
            name: comp.name,
            competitionDate: comp.competitionDate,
            location: comp.location ?? undefined,
            status: newStatus,
            tieBreakerEnabled: comp.tieBreakerEnabled,
            penaltyTrackingMode: comp.penaltyTrackingMode,
            stafetaDvojicAttempts: comp.stafetaDvojicAttempts,
            stafetaDvojicParallelTracks: comp.stafetaDvojicParallelTracks,
            npPenaltyRank: comp.npPenaltyRank,
          })
        : Promise.reject(),
    onSuccess: () => {
      enqueueSnackbar('Stav změněn', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', compId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competitions'] });
      setPendingStatus(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!comp) return Promise.reject();
      const trimmedPublishAt = editForm.publishAt.trim();
      const hadPublishAt = !!comp.publishAt;
      return adminApi.updateCompetition(comp.id, {
        name: editForm.name.trim(),
        competitionDate: editForm.competitionDate,
        location: editForm.location.trim() || undefined,
        status: comp.status,
        tieBreakerEnabled: comp.tieBreakerEnabled,
        penaltyTrackingMode: comp.penaltyTrackingMode,
        stafetaDvojicAttempts: comp.stafetaDvojicAttempts,
        stafetaDvojicParallelTracks: comp.stafetaDvojicParallelTracks,
        npPenaltyRank: comp.npPenaltyRank,
        overallRankingVisible: comp.overallRankingVisible,
        publicVisible: editForm.publicVisible,
        publishAt: trimmedPublishAt ? `${trimmedPublishAt}:00` : undefined,
        clearPublishAt: !trimmedPublishAt && hadPublishAt ? true : undefined,
        // Posila se vzdy; backend vynuti TRUE pokud unit nema canHideFromLanding capability.
        landingVisible: editForm.landingVisible,
        headJudgeName: editForm.headJudgeName.trim(),
        eventLeaderName: editForm.eventLeaderName.trim(),
        commissarName: editForm.commissarName.trim(),
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Soutěž upravena', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', compId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competitions'] });
      setEditOpen(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při ukládání', { variant: 'error' }),
  });

  useEffect(() => {
    if (editOpen && comp) {
      setEditForm({
        name: comp.name,
        competitionDate: comp.competitionDate,
        location: comp.location ?? '',
        publicVisible: comp.publicVisible,
        publishAt: comp.publishAt ? comp.publishAt.slice(0, 16) : '',
        landingVisible: comp.landingVisible ?? true,
        headJudgeName: comp.headJudgeName ?? '',
        eventLeaderName: comp.eventLeaderName ?? '',
        commissarName: comp.commissarName ?? '',
      });
    }
  }, [editOpen, comp]);

  if (isLoading || !comp) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{comp.name} | Admin</title>
      </Helmet>

      {/* Compact header: back + title + status chip + action menu */}
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <IconButton onClick={() => navigate(backTarget)} size="small" sx={{ mt: 0.25 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}
            >
              {comp.name}
            </Typography>
            {!isRecorder ? (
              <Chip
                icon={<CircleIcon sx={{ fontSize: '0.6rem !important' }} />}
                label={COMPETITION_STATUS_LABELS[comp.status]}
                color={STATUS_COLOR[comp.status]}
                onClick={(e) => setStatusAnchor(e.currentTarget)}
                disabled={statusMutation.isPending}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              />
            ) : (
              <Chip
                icon={<CircleIcon sx={{ fontSize: '0.6rem !important' }} />}
                label={COMPETITION_STATUS_LABELS[comp.status]}
                color={STATUS_COLOR[comp.status]}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>
          {(() => {
            const dateFormatted = formatDateCz(comp.competitionDate);
            const hasDateInName = /\b\d{1,2}\.\s?\d{1,2}\.\s?\d{4}\b/.test(comp.name);
            const showDate = !hasDateInName && !!dateFormatted;
            const showLocation = !!comp.location;
            return (showDate || showLocation) ? (
              <Stack
                direction="row"
                gap={1.5}
                flexWrap="wrap"
                sx={{ mt: 0.75, color: 'text.secondary', fontSize: '0.875rem' }}
              >
                {showDate && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonthIcon sx={{ fontSize: 16 }} />
                    {dateFormatted}
                  </Box>
                )}
                {showLocation && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <PlaceIcon sx={{ fontSize: 16 }} />
                    {comp.location}
                  </Box>
                )}
              </Stack>
            ) : null;
          })()}

          {/* Public link row — own line, handles mobile wrap gracefully */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={{ xs: 0.25, sm: 0.75 }}
            sx={{ mt: 0.75, color: 'text.secondary', fontSize: '0.875rem', minWidth: 0 }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <LinkIcon sx={{ fontSize: 16 }} />
              <Typography variant="inherit" component="span">
                Odkaz pro diváky:
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Box
                component="a"
                href={`/s/${comp.shortCode}`}
                target="_blank"
                rel="noreferrer"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {window.location.host}/s/{comp.shortCode}
              </Box>
              <Tooltip title="Kopírovat odkaz">
                <IconButton
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}
                  onClick={() => {
                    const url = `${window.location.origin}/s/${comp.shortCode}`;
                    navigator.clipboard.writeText(url).then(
                      () => enqueueSnackbar('Odkaz zkopírován', { variant: 'success' }),
                      () => enqueueSnackbar('Kopírování selhalo', { variant: 'error' }),
                    );
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Otevřít v nové záložce">
                <IconButton
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}
                  component="a"
                  href={`/s/${comp.shortCode}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
        {!isRecorder && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Upravit
            </Button>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              size="small"
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        )}
      </Stack>

      {/* Status change menu */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
      >
        {STATUSES.map((s) => (
          <MenuItem
            key={s}
            selected={s === comp.status}
            onClick={() => {
              setStatusAnchor(null);
              if (s !== comp.status && !statusMutation.isPending) {
                setPendingStatus(s);
              }
            }}
            disabled={statusMutation.isPending}
          >
            <ListItemIcon>
              <CircleIcon
                fontSize="small"
                color={STATUS_COLOR[s] === 'default' ? 'disabled' : STATUS_COLOR[s]}
                sx={{ fontSize: 12 }}
              />
            </ListItemIcon>
            <ListItemText>{COMPETITION_STATUS_LABELS[s]}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Overflow menu (mobile) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setEditOpen(true);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upravit soutěž</ListItemText>
        </MenuItem>
      </Menu>

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v as MainTab)}
        sx={{
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: { xs: 48, sm: 40 },
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 40 },
            py: 0.5,
            px: { xs: 1, sm: 2 },
            fontSize: '0.8125rem',
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 0,
          },
          '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 18 } },
          '& .tab-label': { display: { xs: 'none', sm: 'inline' } },
        }}
        variant={isMobile ? 'fullWidth' : 'scrollable'}
        scrollButtons="auto"
      >
        <Tab
          value="setup"
          icon={<SettingsIcon />}
          iconPosition="start"
          label={<Box component="span" className="tab-label">Nastavení</Box>}
          aria-label="Nastavení"
        />
        <Tab
          value="results"
          icon={<TimerIcon />}
          iconPosition="start"
          label={<Box component="span" className="tab-label">Výsledky</Box>}
          aria-label="Výsledky"
        />
        <Tab
          value="overall"
          icon={<EmojiEventsIcon />}
          iconPosition="start"
          label={<Box component="span" className="tab-label">Celkové pořadí</Box>}
          aria-label="Celkové pořadí"
        />
        {!isRecorder && (
          <Tab
            value="sharing"
            icon={<ShareIcon />}
            iconPosition="start"
            label={<Box component="span" className="tab-label">Sdílení</Box>}
            aria-label="Sdílení"
          />
        )}
        {!isRecorder && (
          <Tab
            value="history"
            icon={<HistoryIcon />}
            iconPosition="start"
            label={<Box component="span" className="tab-label">Historie</Box>}
            aria-label="Historie"
          />
        )}
      </Tabs>

      {mainTab === 'setup' && (
        <>
          <SubNavPills
            value={setupTab}
            onChange={setSetupTab}
            items={[
              ...(!isRecorder ? [{ value: 0, label: 'Kategorie a disciplíny' }] : []),
              { value: 1, label: 'Družstva' },
              ...(!isRecorder ? [{ value: 4, label: 'Závodníci' }] : []),
              ...(!isRecorder ? [{ value: 5, label: 'Jednotlivci' }] : []),
              ...(!isRecorder ? [{ value: 2, label: 'Zapisovatelé' }] : []),
              ...(!isRecorder && comp.penaltyTrackingMode === 'DETAILED'
                ? [{ value: 3, label: 'Trestné body' }]
                : []),
            ]}
          />

          {setupTab === 0 && !isRecorder && <CompetitionCategoriesTab competitionId={comp.id} categories={categories} />}
          {setupTab === 1 && <CompetitionTeamsTab competitionId={comp.id} categories={categories} />}
          {setupTab === 4 && !isRecorder && (
            <CompetitionAthletesTab competitionId={comp.id} categories={categories} />
          )}
          {setupTab === 5 && !isRecorder && (
            <CompetitionIndividualEntriesTab categories={categories} />
          )}
          {setupTab === 2 && !isRecorder && <CompetitionRecordersTab competition={comp} />}
          {setupTab === 3 && !isRecorder && comp.penaltyTrackingMode === 'DETAILED' && (
            <CompetitionPenaltyCatalogTab competitionId={comp.id} categories={categories} />
          )}
        </>
      )}

      {mainTab === 'results' && <CompetitionResultsTab competition={comp} />}

      {mainTab === 'overall' && <CompetitionOverallTab competition={comp} />}

      {mainTab === 'sharing' && !isRecorder && (
        <>
          <SubNavPills
            value={sharingTab}
            onChange={setSharingTab}
            items={[
              { value: 0, label: 'QR kód' },
              { value: 1, label: 'Export' },
              { value: 2, label: 'Layout pro diváky' },
            ]}
          />

          {sharingTab === 0 && (
            <Card>
              <CardContent>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="h6">QR kód pro diváky</Typography>
                  <img
                    src={`/api/admin/competitions/${comp.id}/qr?size=300`}
                    alt={`QR kód ${comp.shortCode}`}
                    style={{ maxWidth: 300 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Kód: <strong>{comp.shortCode}</strong> • URL: {window.location.origin}/s/{comp.shortCode}
                  </Typography>
                  <Button
                    variant="outlined"
                    href={`/api/admin/competitions/${comp.id}/qr?size=600`}
                    download={`qr-${comp.shortCode}.png`}
                  >
                    Stáhnout PNG
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {sharingTab === 1 && (
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Export výsledků</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CSV obsahuje výsledky ve všech kategoriích a disciplínách + celkové pořadí.
                    Soubor je ve formátu CSV s UTF-8 BOM, oddělovač středník — kompatibilní s Excelem.
                  </Typography>
                  <Button
                    variant="contained"
                    href={`/api/admin/competitions/${comp.id}/export/csv`}
                    download={`vysledky-${comp.shortCode}.csv`}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Stáhnout CSV
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    PDF a XLSX export bude dostupný v dalších verzích.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {sharingTab === 2 && <CompetitionDisplaySettingsTab competition={comp} />}
        </>
      )}

      {mainTab === 'history' && !isRecorder && <CompetitionAuditTab competitionId={comp.id} />}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upravit soutěž</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Název soutěže"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              autoFocus
              fullWidth
            />
            <TextField
              label="Datum konání"
              type="date"
              value={editForm.competitionDate}
              onChange={(e) => setEditForm((f) => ({ ...f, competitionDate: e.target.value }))}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Místo"
              value={editForm.location}
              onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="např. Hřiště SDH Těškovice"
              fullWidth
            />

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Veřejná viditelnost
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.publicVisible}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, publicVisible: e.target.checked }))
                  }
                />
              }
              label="Soutěž je veřejně dostupná"
            />
            {editForm.publicVisible ? (
              <>
                <TextField
                  label="Naplánovat zveřejnění"
                  type="datetime-local"
                  size="small"
                  value={editForm.publishAt}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, publishAt: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="Necháte-li prázdné, soutěž je zveřejněná ihned."
                />
                {editForm.publishAt && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => setEditForm((f) => ({ ...f, publishAt: '' }))}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Odstranit plánování
                  </Button>
                )}
                {comp.unitCanHideFromLanding && (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!editForm.landingVisible}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, landingVisible: !e.target.checked }))
                          }
                        />
                      }
                      label="Skrýt z rozcestníku souteze.hasicionline.cz"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                      Soutěž zůstane plně přístupná přes přímý odkaz, ale nezobrazí se v seznamu
                      probíhajících soutěží na úvodní stránce.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="warning" variant="outlined">
                Soutěž je skrytá. Diváci nemohou otevřít veřejnou stránku ani ji najít v seznamu. Odkaz na plat (televize/projektor) zůstává funkční.
              </Alert>
            )}

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Oficiálové soutěže (pro PDF výsledkovku)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Vyplňte pro tisk PDF s podpisy. Necháte-li prázdné, v PDF zůstane prázdný řádek pro ruční podpis.
            </Typography>
            <TextField
              label="Hlavní rozhodčí"
              value={editForm.headJudgeName}
              onChange={(e) => setEditForm((f) => ({ ...f, headJudgeName: e.target.value }))}
              placeholder="Jméno a příjmení"
              size="small"
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Velitel soutěže"
              value={editForm.eventLeaderName}
              onChange={(e) => setEditForm((f) => ({ ...f, eventLeaderName: e.target.value }))}
              placeholder="Jméno a příjmení"
              size="small"
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Komisař soutěže"
              value={editForm.commissarName}
              onChange={(e) => setEditForm((f) => ({ ...f, commissarName: e.target.value }))}
              placeholder="Jen pro krajská/republiková kola"
              size="small"
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editMutation.isPending}>
            Zrušit
          </Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={
              editMutation.isPending || !editForm.name.trim() || !editForm.competitionDate
            }
          >
            {editMutation.isPending ? 'Ukládám…' : 'Uložit'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pendingStatus !== null}
        title="Změnit stav soutěže?"
        message={
          pendingStatus
            ? `Opravdu chcete změnit stav soutěže z „${COMPETITION_STATUS_LABELS[comp.status]}" na „${COMPETITION_STATUS_LABELS[pendingStatus]}"?` +
              (pendingStatus === 'LIVE'
                ? ' Soutěž bude veřejně viditelná jako „Probíhá".'
                : pendingStatus === 'FINISHED'
                  ? ' Soutěž bude označena jako dokončená a celkové pořadí se zveřejní.'
                  : ' Soutěž se vrátí do režimu úprav.')
            : ''
        }
        confirmText="Změnit stav"
        destructive={pendingStatus === 'FINISHED'}
        loading={statusMutation.isPending}
        onCancel={() => setPendingStatus(null)}
        onConfirm={() => pendingStatus && statusMutation.mutate(pendingStatus)}
      />
    </>
  );
};

interface SubNavPillsProps {
  value: number;
  onChange: (v: number) => void;
  items: { value: number; label: string }[];
}

const SubNavPills = ({ value, onChange, items }: SubNavPillsProps) => (
  <Box sx={{ mb: 2 }}>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, v) => {
        if (v !== null) onChange(v);
      }}
      sx={{
        flexWrap: 'wrap',
        gap: 0.5,
        '& .MuiToggleButton-root': {
          border: 0,
          borderRadius: 999,
          px: 2,
          py: 0.5,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: 'text.secondary',
          bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
          },
        },
      }}
    >
      {items.map((it) => (
        <ToggleButton key={it.value} value={it.value}>
          {it.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);
